import {
  ComponentRef,
  EmbeddedViewRef,
  EventEmitter,
  NgZone
} from "@angular/core";
import { Subscription } from "rxjs";

import { OverlayContext } from "./overlay-context";
import {
  PositionCoords,
  PositionOption,
  PositionStrategy,
  PositionStrategyResult
} from "./position-strategies";

const DIRECTIONS: string[] = ["top", "bottom", "left", "right"];

/**
 * TODO: document this
 *
 * @export
 * @abstract
 * @class OverlayRef
 */
export abstract class OverlayRef {
  private destroyed: boolean = false;
  private destroyFn?: () => void;

  /**
   * TODO: document this
   *
   * @readonly
   * @type {(PositionOption | undefined)}
   */
  get currentPosition(): PositionOption | undefined {
    return this._currentPosition;
  }
  private _currentPosition: PositionOption | undefined;

  /**
   * TODO: document this
   *
   * @type {EventEmitter<PositionOption>}
   */
  currentPositionChange: EventEmitter<PositionOption> = new EventEmitter<
    PositionOption
  >();

  /**
   * @constructor
   */
  constructor(
    public nativeElement: HTMLElement,
    private positionStrategy: PositionStrategy,
    private windowRef: Window,
    private ngZone: NgZone
  ) {
    nativeElement.classList.add("overlay");
    nativeElement.style.position = "fixed";
    nativeElement.style.zIndex = "9999";
  }

  private initted: boolean = false;
  private ngZoneStableSubscription?: Subscription;

  /**
   * @internal
   */
  init(): void {
    if (this.initted) {
      throw new Error("Cannot init already initialized overlay");
    }
    this.initted = true;

    this.ngZoneStableSubscription = this.ngZone.onStable.subscribe(() => {
      // TODO: check if we should run this outside angular zone?
      this.position();
    });

    this.windowRef.addEventListener("resize", this.handleResize);

    this.position();
  }

  /**
   * @internal
   */
  destroy(): void {
    if (this.destroyed) {
      throw new Error("Cannot destroy already destroyed overlay");
    }
    this.destroyed = true;

    this.windowRef.removeEventListener("resize", this.handleResize);

    if (this.ngZoneStableSubscription != null) {
      this.ngZoneStableSubscription.unsubscribe();
    }

    if (this.destroyFn != null) {
      this.destroyFn();
    }

    this.nativeElement.remove();
  }

  private handleResize: () => void = () => {
    this.position(true);
  };

  private prevClientRect?: ClientRect;

  /**
   * TODO: document this
   *
   * @param {boolean} [force]
   * @returns {void}
   */
  position(force?: boolean): void {
    if (this.destroyed) {
      throw new Error("Cannot position already destroyed overlay");
    }

    let clientRect: ClientRect = this.nativeElement.getBoundingClientRect();

    if (this.prevClientRect != null && !force) {
      let hasChanges: boolean = DIRECTIONS.some(
        (direction: keyof ClientRect) => {
          return this.prevClientRect![direction] !== clientRect[direction];
        }
      );
      if (!hasChanges) {
        return;
      }
    }
    this.prevClientRect = clientRect;

    let result: PositionStrategyResult = this.positionStrategy.getPosition(
        clientRect,
        this.windowRef
      ),
      positionCoords: PositionCoords = result.coords;

    DIRECTIONS.forEach((direction: keyof PositionCoords) => {
      this.nativeElement.style[direction] =
        positionCoords[direction] != null
          ? `${positionCoords[direction]}px`
          : "";
    });

    if (this._currentPosition !== result.position) {
      this._currentPosition = result.position;
      this.currentPositionChange.emit(result.position);
    }
  }

  /**
   * @internal
   */
  setDestroyFn(fn: () => void): void {
    if (this.destroyFn != null) {
      throw new Error("Cannot set overlay destroy function again");
    }
    this.destroyFn = fn;
  }
}

export class ComponentOverlayRef<C> extends OverlayRef {
  componentRef: ComponentRef<C>;
}

export class ViewOverlayRef<C> extends OverlayRef {
  viewRef: EmbeddedViewRef<OverlayContext<C>>;
}
